import React, { useState } from "react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { ChevronDown, ChevronRight } from "lucide-react";
import ChartCardContainer from "../charts/ChartCardContainer";
import { formatPrice } from "../../utils/formatPrice";
import "../../css/model.css";

const MiniChart = ({ data, change }) => {
  const isPositive = (change || 0) >= 0;
  const strokeColor = isPositive ? "#10B981" : "#EF4444";

  if (!data || data.length === 0) {
    return <div className="model-breakdown__mini-chart-empty" />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={strokeColor}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const StabilityIndicator = ({ stability }) => {
  const bars = stability?.bars?.length
    ? stability.bars
    : Array.from({ length: 4 }, () => ({ filled: false }));

  return (
    <div className="model-breakdown__stability">
      {bars.map((bar, index) => (
        <div
          key={index}
          className="model-breakdown__stability-segment"
          style={{ backgroundColor: bar.filled ? "#f97316" : "#4b5563" }}
        />
      ))}
    </div>
  );
};

const MaterialsTable = ({ components, sectionIndex }) => (
  <table className="model-breakdown__table">
    <thead>
      <tr>
        <th>Name</th>
        <th>Code</th>
        <th>Stability</th>
        <th>Graph</th>
        <th>Price</th>
        <th>Rate</th>
        <th>Amount</th>
        <th>M.Price</th>
        <th>Source</th>
      </tr>
    </thead>
    <tbody>
      {components.map((component) => {
        const change = component.change || 0;
        const isPositive = change >= 0;
        const source = component.source || '—';
        const code = component.code || '—';
        const amount = component.amount ?? 0;
        const price = component.price || 0;
        const rate = component.rate || 0;
        const marketPrice = component.marketPrice || 0;
        const displayCurrency = component.priceCurrency || component.currency || '—';
        const materialHref = component.materialCode ? `/charts/material/${component.materialCode}` : null;

        return (
          <tr key={`${sectionIndex}-${component.id}`}>
            <td>
              {materialHref ? (
                <a
                  href={materialHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="model-breakdown__component-name model-breakdown__component-name--link"
                >
                  {component.name}
                </a>
              ) : (
                <div className="model-breakdown__component-name">{component.name}</div>
              )}
            </td>
            <td>
              <div className="model-breakdown__component-code">{code}</div>
            </td>
            <td>
              <StabilityIndicator stability={component.stability} />
            </td>
            <td>
              <div className="model-breakdown__graph-cell">
                <div className="model-breakdown__mini-chart">
                  <MiniChart data={component.chartData || []} change={change} />
                </div>
                {change !== 0 && (
                  <div className={`model-breakdown__change ${isPositive ? 'model-breakdown__change--positive' : 'model-breakdown__change--negative'}`}>
                    {isPositive ? '+' : ''}{change}%
                  </div>
                )}
              </div>
            </td>
            <td>
              <div className="model-breakdown__price">{price ? formatPrice(price) : '—'}</div>
              <div className="model-breakdown__component-code">{displayCurrency}</div>
            </td>
            <td>
              <div className="model-breakdown__price">{rate ? formatPrice(rate) : '—'}</div>
              <div className="model-breakdown__component-code">{displayCurrency}</div>
            </td>
            <td>
              <div className="model-breakdown__price">
                {amount ? formatPrice(amount) : '—'} {component.unit || ''}
              </div>
            </td>
            <td>
              <div className="model-breakdown__price model-breakdown__price--market">
                {marketPrice ? formatPrice(marketPrice) : '—'}
              </div>
            </td>
            <td>
              <div className="model-breakdown__source">{source}</div>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const SubProductSection = ({ section, sectionIndex, onToggleInChart, activeComparisonCodes }) => {
  const [expanded, setExpanded] = useState(false);
  const isActive = activeComparisonCodes.includes(String(section.productRefId));

  return (
    <div className="model-breakdown__section">
      <div
        className="model-breakdown__section-header model-breakdown__section-header--collapsible"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="model-breakdown__section-title-group">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="model-breakdown__section-title">{section.title}</span>
        </div>
        <div className="model-breakdown__section-actions" onClick={e => e.stopPropagation()}>
          {section.price && (
            <span className="model-breakdown__section-price">
              {typeof section.price === 'number' ? formatPrice(section.price) : section.price}
              <span className="model-breakdown__section-currency">{section.currency} /mt</span>
            </span>
          )}
          <button
            className={`model-breakdown__show-chart-btn ${isActive ? 'active' : ''}`}
            onClick={() => onToggleInChart?.(section)}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="6" cy="6" r="5" stroke="#10B981" strokeWidth="2"/>
            </svg>
            <span>{isActive ? 'Hide from Chart' : 'Show in Chart'}</span>
          </button>
        </div>
      </div>

      {expanded && section.components.length > 0 && (
        <MaterialsTable components={section.components} sectionIndex={sectionIndex} />
      )}
    </div>
  );
};

const ModelBreakdownTable = ({ sections, onToggleInChart, activeComparisonCodes = [] }) => {
  if (!sections || sections.length === 0) return null;

  return (
    <ChartCardContainer title="MODEL BREAKDOWN" showActions={true}>
      <div className="model-breakdown">
        {sections.map((section, sectionIndex) => {
          // Sub-product section → collapsible with header
          if (section.productRefId) {
            return (
              <SubProductSection
                key={sectionIndex}
                section={section}
                sectionIndex={sectionIndex}
                onToggleInChart={onToggleInChart}
                activeComparisonCodes={activeComparisonCodes}
              />
            );
          }

          // Direct materials → no heading, just the table
          return section.components.length > 0 ? (
            <div key={sectionIndex} className="model-breakdown__section">
              <MaterialsTable components={section.components} sectionIndex={sectionIndex} />
            </div>
          ) : null;
        })}
      </div>
    </ChartCardContainer>
  );
};

export default ModelBreakdownTable;
